/**
 * Mandanten-Theming zur Laufzeit (design/mandanten-theming.md).
 *
 * Die sechs Mandanten der VERCH-Gruppe haben vorgerechnete, kontrastgeprüfte
 * Akzent-Sets in tokens.css (`:root[data-tenant=<slug>]`). Für einen Mandanten
 * ohne vorgerechnetes Set leitet `deriveAccentSet` dieselben neun Variablen aus
 * seiner Markenfarbe ab — in OKLCH, mit Kontrastprüfung nach WCAG 2.x.
 */
import { ACCENT_NAMES, TENANT_ACCENT_SETS } from './tenant-sets';
function hexToRgb(hex) {
    let h = hex.trim().replace('#', '');
    if (h.length === 3)
        h = [...h].map((c) => c + c).join('');
    const n = parseInt(h.slice(0, 6), 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}
function rgbToHex([r, g, b]) {
    const k = (v) => Math.round(Math.max(0, Math.min(1, v)) * 255)
        .toString(16)
        .padStart(2, '0');
    return `#${k(r)}${k(g)}${k(b)}`;
}
const toLinear = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const fromLinear = (c) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);
function rgbToOklch(rgb) {
    const [r, g, b] = rgb.map(toLinear);
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
    const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
    const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
    return [L, Math.hypot(A, B), Math.atan2(B, A)];
}
function oklchToRgb([L, C, H]) {
    const A = C * Math.cos(H);
    const B = C * Math.sin(H);
    const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
    const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
    const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
    return [
        fromLinear(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
        fromLinear(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
        fromLinear(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
    ];
}
function luminance(hex) {
    const [r, g, b] = hexToRgb(hex).map(toLinear);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contrast(a, b) {
    const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
    return (x + 0.05) / (y + 0.05);
}
/** Helligkeit in Schritten verschieben, bis `ok` gilt (höchstens bis zum Rand). */
function shiftUntil(hex, richtung, ok) {
    const [L, C, H] = rgbToOklch(hexToRgb(hex));
    for (let i = 0; i <= 60; i++) {
        const kandidat = rgbToHex(oklchToRgb([Math.max(0, Math.min(1, L + richtung * i * 0.01)), C, H]));
        if (ok(kandidat))
            return kandidat;
    }
    return richtung > 0 ? '#ffffff' : '#000000';
}
function withLightness(hex, L, maxChroma) {
    const [, C, H] = rgbToOklch(hexToRgb(hex));
    return rgbToHex(oklchToRgb([L, Math.min(C, maxChroma), H]));
}
const FLAECHEN = {
    light: { surface: '#f5f7fb', raised: '#ffffff', ink: '#10203c' },
    dark: { surface: '#0b1220', raised: '#121a2c', ink: '#10203c' },
};
/**
 * Leitet die neun Akzent-Variablen aus einer Markenfarbe ab (Regeln 1–6 in
 * design/mandanten-theming.md). Deterministisch: gleiche Farbe, gleiche Werte.
 */
export function deriveAccentSet(brand, theme) {
    const f = FLAECHEN[theme];
    const dunkler = theme === 'light' ? -1 : 1;
    const textAuf = (fill) => contrast('#ffffff', fill) >= contrast(f.ink, fill) ? '#ffffff' : f.ink;
    const fuellungOk = (h) => Math.max(contrast('#ffffff', h), contrast(f.ink, h)) >= 4.5;
    const accent = fuellungOk(brand) ? brand : shiftUntil(brand, dunkler, fuellungOk);
    const onAccent = textAuf(accent);
    const [La] = rgbToOklch(hexToRgb(accent));
    const sehrHell = La > 0.8;
    const hoverRichtung = theme === 'light' || sehrHell ? -1 : 1;
    const verschoben = (d) => {
        const [L, C, H] = rgbToOklch(hexToRgb(accent));
        return rgbToHex(oklchToRgb([L + hoverRichtung * d, C, H]));
    };
    const textOk = (h) => contrast(h, f.surface) >= 4.5 && contrast(h, f.raised) >= 4.5;
    const accentInk = textOk(brand) ? brand : shiftUntil(brand, dunkler, textOk);
    const soft = withLightness(brand, theme === 'light' ? 0.955 : 0.27, theme === 'light' ? 0.04 : 0.06);
    const softStrong = withLightness(brand, theme === 'light' ? 0.9 : 0.33, theme === 'light' ? 0.07 : 0.08);
    const softInk = contrast(accentInk, soft) >= 4.5
        ? accentInk
        : shiftUntil(accentInk, dunkler, (h) => contrast(h, soft) >= 4.5);
    const lineOk = (h) => contrast(h, f.raised) >= 3;
    const accentLine = lineOk(brand) ? brand : shiftUntil(brand, dunkler, lineOk);
    return {
        accent,
        'accent-hover': verschoben(0.06),
        'accent-press': verschoben(0.12),
        'on-accent': onAccent,
        'accent-ink': accentInk,
        'accent-soft': soft,
        'accent-soft-strong': softStrong,
        'accent-soft-ink': softInk,
        'accent-line': accentLine,
    };
}
/** Das Akzent-Set eines Mandanten: vorgerechnet, sonst abgeleitet. */
export function accentSetFor(slug, brand, theme) {
    const fertig = slug ? TENANT_ACCENT_SETS[slug] : undefined;
    if (fertig)
        return fertig[theme];
    return deriveAccentSet(brand ?? '#0b5fd6', theme);
}
/** Markenfarbe eines Mandanten (aus dem Markenpaket, sonst die gelieferte Farbe). */
export function brandColorFor(slug, fallback) {
    return (slug && TENANT_ACCENT_SETS[slug]?.brand) || fallback;
}
/**
 * Setzt Theme und Mandanten-Akzent auf ein Element (in der Shell: `<html>`).
 * Für vorgerechnete Mandanten genügt `data-tenant`; für unbekannte schreibt die
 * Funktion die neun Variablen als Inline-Custom-Properties.
 */
export function applyTheme(el, opts) {
    el.dataset.theme = opts.theme;
    const vorgerechnet = Boolean(opts.tenantSlug && TENANT_ACCENT_SETS[opts.tenantSlug]);
    if (opts.tenantSlug)
        el.dataset.tenant = opts.tenantSlug;
    else
        delete el.dataset.tenant;
    for (const n of ACCENT_NAMES)
        el.style.removeProperty(`--${n}`);
    if (opts.tenantSlug && !vorgerechnet && opts.tenantColor) {
        const set = deriveAccentSet(opts.tenantColor, opts.theme);
        for (const n of ACCENT_NAMES)
            el.style.setProperty(`--${n}`, set[n]);
    }
}
/** System-Einstellung auflösen. */
export function resolveTheme(pref) {
    if (pref !== 'system')
        return pref;
    return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
}
export { TENANT_ACCENT_SETS, ACCENT_NAMES };
